/**
 * Assessment category classification — shared between client (dashboard
 * governance tabs) and server (category-based reviewer/approver routing).
 *
 * An assessment's category is derived from its policy labels. The same
 * priority order is used everywhere so a given assessment maps to exactly one
 * category: screening → assurance(testing) → monitoring → (default) assessment.
 */

/** Dashboard governance buckets (mutually exclusive per assessment). */
export type GovernanceTab =
  | 'assessments'
  | 'screening'
  | 'testing'
  | 'monitoring'
  | 'mitigation';

/**
 * Classify by policy labels (case-insensitive). Priority order guarantees a
 * single bucket per assessment.
 */
export function governanceTabFromLabels(
  labels: readonly (string | null | undefined)[] | null | undefined
): GovernanceTab {
  const lower = (labels ?? [])
    .filter((l): l is string => typeof l === 'string' && l.length > 0)
    .map((l) => l.toLowerCase());
  if (lower.includes('screening')) {
    return 'screening';
  }
  if (lower.includes('assurance')) {
    return 'testing';
  }
  if (lower.includes('monitoring')) {
    return 'monitoring';
  }
  if (lower.includes('mitigation') || lower.includes('notification-alerts')) {
    return 'mitigation';
  }
  return 'assessments';
}

/**
 * The org project-role-slot category that reviews/approves a given governance
 * tab's assessments:
 *  - screening  → Governance
 *  - monitoring → Governance
 *  - testing (assurance) → Assurance
 *  - assessments (normal) → Compliance
 */
export type ReviewSlotCategory = 'GOVERNANCE' | 'COMPLIANCE' | 'ASSURANCE';

export function slotCategoryForTab(tab: GovernanceTab): ReviewSlotCategory {
  switch (tab) {
    case 'screening':
    case 'monitoring':
      return 'GOVERNANCE';
    case 'testing':
      return 'ASSURANCE';
    case 'assessments':
    default:
      return 'COMPLIANCE';
  }
}

/** Human label for a slot category, for UI/plans and skip messages. */
export const SLOT_CATEGORY_LABEL: Record<ReviewSlotCategory, string> = {
  GOVERNANCE: 'Governance',
  COMPLIANCE: 'Compliance',
  ASSURANCE: 'Assurance',
};
